import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';
import { CloudSyncSettings } from '../components/CloudSyncSettings';
import { ConfirmationModal } from '../components/ui/ConfirmationModal';
import { AuthModal } from '../components/auth/AuthModal';
import { LanguageSwitcher } from '../components/ui/LanguageSwitcher';
import { useToast } from '../components/Toast';
import { useDarkMode } from '../hooks/useDarkMode';
import { useUserMode } from '../hooks/useUserMode';
import { usePageVisibility } from '../hooks/usePageVisibility';
import { usePageModules } from '../hooks/usePageModules';
import { useConfirmation } from '../hooks/useConfirmation';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrencyContext, CURRENCIES, type Currency } from '../contexts/CurrencyContext';
import { useAuthStore } from '../store/authStore';
import { useHostingSubscriptions } from '../hooks/useHostingSubscriptions';
import { useIsMobile } from '../hooks/useIsMobile';
import * as profileApi from '../services/profileApi';
import type { ApiKeyData } from '../services/profileApi';
import { useFormState } from '../hooks/useFormState';
import { FormField } from '../components/ui/FormField';
import styles from './Settings.module.css';

interface SettingsProps {
  onNavigateToAPI?: () => void;
  /** Imperative navigation — used by the V2.A banner that points to /account. */
  onNavigate?: (pageId: string) => void;
}

export function Settings({ onNavigateToAPI, onNavigate }: SettingsProps) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { currency, setCurrency } = useCurrencyContext();
  const [isDark, toggleDarkMode] = useDarkMode();
  const [userMode, setUserMode] = useUserMode();
  const [pageVisibility, setPageVisibility] = usePageVisibility();
  const { enableModule, disableModule } = usePageModules();
  const { isAuthenticated } = useAuthStore();
  const { confirm, confirmationProps } = useConfirmation();
  const toast = useToast();
  const isMobile = useIsMobile();
  const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth);

  // Hosting subscriptions
  const {
    subscriptions: hostingSubscriptions,
    hasActiveSubscription,
  } = useHostingSubscriptions();

  // Banner / API-key error surface (Profile + Security have moved to MyAccount in V2.A3.5).
  const [profileError, setProfileError] = useState<string | null>(null);

  // API Keys state
  const [apiKeys, setApiKeys] = useState<ApiKeyData[]>([]);
  const [isLoadingKeys, setIsLoadingKeys] = useState(false);
  const [isCreatingKey, setIsCreatingKey] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [showNewKey, setShowNewKey] = useState(false);

  // V2.I.13.a — API key form state via useFormState (single field but
  // shows the migration pattern : validation rules declared in one place,
  // error display routed through <FormField>).
  const apiKeyForm = useFormState<{ name: string }>({
    initial: { name: '' },
    validate: {
      name: (v) => {
        const trimmed = v.trim();
        if (trimmed.length === 0) {
          return language === 'fr' ? 'Le nom est requis' : 'Name is required';
        }
        if (trimmed.length > 60) {
          return language === 'fr' ? 'Maximum 60 caractères' : 'Max 60 characters';
        }
        return null;
      },
    },
  });
  const [keyCopied, setKeyCopied] = useState(false);

  // Auth modal state
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  // Load API keys on mount
  useEffect(() => {
    if (isAuthenticated) {
      loadApiKeys();
    }
  }, [isAuthenticated]);

  // Track portrait/landscape orientation changes
  useEffect(() => {
    const handleResize = () => {
      setIsPortrait(window.innerHeight > window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadApiKeys = async () => {
    setIsLoadingKeys(true);
    try {
      const keys = await profileApi.listApiKeys();
      setApiKeys(keys);
    } catch (error: any) {
      // Silently fail if API not available
      console.warn('Failed to load API keys (API may not be deployed yet):', error.message);
    } finally {
      setIsLoadingKeys(false);
    }
  };

  const handleCreateApiKey = async () => {
    // V2.I.13.a : centralised validation through useFormState. validateAll()
    // surfaces the error via FormField (was a silent early-return before).
    if (!apiKeyForm.validateAll()) return;

    setIsCreatingKey(true);
    try {
      const newKey = await profileApi.createApiKey({ name: apiKeyForm.values.name.trim() });
      setApiKeys(prev => [newKey, ...prev]);
      setNewlyCreatedKey(newKey.key || null);
      setShowNewKey(true);
      apiKeyForm.reset();
      // V2.I.5.2 : explicit success feedback (was silent — only the modal opened).
      toast(language === 'fr' ? 'Clé API créée ✓' : 'API key created ✓', 'success');
    } catch (error: any) {
      setProfileError(error.message);
      // V2.I.13.a : also surface server-side errors at the field level.
      apiKeyForm.setError('name', error.message);
      toast(
        language === 'fr' ? `Échec de la création : ${error.message}` : `Creation failed: ${error.message}`,
        'error',
      );
    } finally {
      setIsCreatingKey(false);
    }
  };

  const handleRevokeApiKey = async (keyId: string) => {
    const confirmed = await confirm({
      title: language === 'fr' ? 'Révoquer la clé API' : 'Revoke API key',
      message: language === 'fr' ? 'Révoquer cette clé API ? Cette action est irréversible.' : 'Revoke this API key? This action cannot be undone.',
      confirmText: language === 'fr' ? 'Révoquer' : 'Revoke',
      cancelText: language === 'fr' ? 'Annuler' : 'Cancel',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      await profileApi.revokeApiKey(keyId);
      setApiKeys(prev => prev.filter(k => k.id !== keyId));
      // V2.I.5.2 : confirm success — was silent, user couldn't tell whether the click registered.
      toast(language === 'fr' ? 'Clé API révoquée' : 'API key revoked', 'info');
    } catch (error: any) {
      setProfileError(error.message);
      toast(
        language === 'fr' ? `Échec de la révocation : ${error.message}` : `Revocation failed: ${error.message}`,
        'error',
      );
    }
  };

  const copyApiKey = async () => {
    if (newlyCreatedKey) {
      await navigator.clipboard.writeText(newlyCreatedKey);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    }
  };

  // V2.I.8.a — the 5 inline-style constants previously declared here
  // (cardStyle, sectionTitleStyle, inputStyle, buttonPrimaryStyle,
  // buttonSecondaryStyle) moved to Settings.module.css. Single source of
  // truth, simpler call sites, easier theming.

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Page Header - Different for logged in vs logged out */}
      {isAuthenticated ? (
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: 700,
            color: 'var(--text)',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Icons.Settings size={32} style={{ color: 'var(--primary)' }} />
            {language === 'fr' ? 'Paramètres' : 'Settings'}
          </h1>
          <p style={{ color: 'var(--muted)', fontSize: '15px' }}>
            {language === 'fr' ? 'Gérez votre compte, vos préférences et votre sécurité' : 'Manage your account, preferences and security'}
          </p>
        </div>
      ) : (
        <div style={{
          marginBottom: '32px',
          padding: '32px',
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(99, 102, 241, 0.1))',
          borderRadius: '16px',
          border: '1px solid rgba(139, 92, 246, 0.2)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Icons.Settings size={32} color="white" />
            </div>
            <div>
              <h1 style={{
                fontSize: '28px',
                fontWeight: 700,
                color: 'var(--text)',
                marginBottom: '4px',
              }}>
                {language === 'fr' ? 'Paramètres' : 'Settings'}
              </h1>
              <p style={{ color: 'var(--muted)', fontSize: '15px' }}>
                {language === 'fr' ? 'Personnalisez votre expérience Edouard.ai' : 'Customize your Edouard.ai experience'}
              </p>
            </div>
          </div>

          {/* Login CTA for non-authenticated users */}
          <div style={{
            padding: '20px',
            background: 'var(--card-bg)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap',
          }}>
            <div style={{ flex: 1, minWidth: '250px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Icons.Sparkles size={20} style={{ color: '#f59e0b' }} />
                <span style={{ fontWeight: 600, color: 'var(--text)' }}>
                  {language === 'fr' ? 'Débloquez plus de fonctionnalités' : 'Unlock more features'}
                </span>
              </div>
              <p style={{ fontSize: '14px', color: 'var(--muted)', lineHeight: '1.5' }}>
                {language === 'fr'
                  ? 'Connectez-vous pour accéder à votre profil, synchroniser vos données sur tous vos appareils, et gérer vos clés API.'
                  : 'Sign in to access your profile, sync your data across devices, and manage your API keys.'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setIsAuthModalOpen(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                  color: 'white',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)',
                }}
              >
                <Icons.LogIn size={18} />
                {language === 'fr' ? 'Se connecter' : 'Sign in'}
              </button>
              <button
                onClick={() => setIsAuthModalOpen(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  background: 'transparent',
                  color: 'var(--primary)',
                  border: '1px solid var(--primary)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <Icons.UserPlus size={18} />
                {language === 'fr' ? 'Créer un compte' : 'Create account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error banner */}
      {profileError && (
        <div style={{
          padding: '12px 16px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#ef4444',
        }}>
          <Icons.AlertCircle size={18} />
          {profileError}
          <button
            onClick={() => setProfileError(null)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}
          >
            <Icons.X size={16} />
          </button>
        </div>
      )}

      {/* V2.A — banner pointing to the new /account page. Sprint V2.A3.5
          completed the migration : Profile + Security + data export are
          now exclusively on /account. This page keeps app preferences,
          API keys, sync, and module visibility. */}
      {isAuthenticated && (
        <div
          role="note"
          style={{
            marginBottom: '20px',
            padding: '12px 16px',
            background: 'rgba(99, 102, 241, 0.08)',
            border: '1px solid rgba(99, 102, 241, 0.25)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          <Icons.Info size={18} style={{ color: '#A5B4FC', flexShrink: 0 }} />
          <span style={{ fontSize: '13px', color: 'var(--text)', flex: 1, minWidth: '240px' }}>
            {language === 'fr'
              ? "Votre identité, votre sécurité et l'export de vos données sont sur "
              : 'Your identity, security and data export live on '}
            <strong>{language === 'fr' ? 'Mon Compte' : 'My Account'}</strong>
            {language === 'fr' ? '.' : '.'}
          </span>
          <button
            type="button"
            onClick={() => onNavigate?.('account')}
            style={{
              padding: '8px 14px',
              background: 'linear-gradient(135deg, #4F46E5, #6366F1)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              flexShrink: 0,
            }}
          >
            {language === 'fr' ? 'Ouvrir Mon Compte' : 'Open My Account'}
            <Icons.ArrowRight size={12} />
          </button>
        </div>
      )}


      {/* API Keys Section (only if authenticated) */}
      {isAuthenticated && (
        <div
          className={styles.card}
          style={{
            marginBottom: '20px',
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.05), transparent)',
            borderColor: 'rgba(245, 158, 11, 0.15)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Icons.Key size={20} color="white" />
            </div>
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text)', margin: 0 }}>
                {language === 'fr' ? 'Clés API' : 'API Keys'}
              </h2>
              <p style={{ fontSize: '13px', color: 'var(--muted)', margin: 0 }}>
                {language === 'fr' ? 'Accédez à vos données via l\'API REST' : 'Access your data via REST API'}
              </p>
            </div>
          </div>

          <p style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '16px' }}>
            {language === 'fr'
              ? 'Générez des clés API pour accéder à vos données via l\'API REST. Maximum 5 clés.'
              : 'Generate API keys to access your data via the REST API. Maximum 5 keys.'}
          </p>

          {/* V2.I.13.a — New key creation form (FormField + useFormState) */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <FormField
                label={language === 'fr' ? 'Nom de la clé' : 'Key name'}
                hint={
                  apiKeys.length >= 5
                    ? language === 'fr'
                      ? 'Limite atteinte (5 max). Révoquez une clé pour en créer une nouvelle.'
                      : 'Limit reached (5 max). Revoke a key to create a new one.'
                    : undefined
                }
                error={apiKeyForm.errorIfTouched('name')}
                required
              >
                <input
                  type="text"
                  placeholder={language === 'fr' ? 'Ex: Production' : 'e.g., Production'}
                  value={apiKeyForm.values.name}
                  onChange={(e) => apiKeyForm.setValue('name', e.target.value)}
                  onBlur={() => apiKeyForm.setTouched('name')}
                  disabled={apiKeys.length >= 5}
                  className={styles.input}
                />
              </FormField>
            </div>
            <button
              onClick={handleCreateApiKey}
              disabled={isCreatingKey || apiKeys.length >= 5}
              className={styles.buttonPrimary}
              style={{ whiteSpace: 'nowrap', opacity: isCreatingKey || apiKeys.length >= 5 ? 0.7 : 1, marginTop: '24px' }}
            >
              {isCreatingKey ? <Icons.Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Icons.Plus size={14} />}
              <span style={{ marginLeft: '6px' }}>{language === 'fr' ? 'Générer' : 'Generate'}</span>
            </button>
          </div>

          {/* Newly created key display */}
          {newlyCreatedKey && (
            <div style={{
              padding: '16px',
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '8px',
              marginBottom: '20px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: '#10b981' }}>
                <Icons.CheckCircle size={18} />
                <span style={{ fontWeight: 600 }}>{language === 'fr' ? 'Clé créée avec succès !' : 'Key created successfully!'}</span>
              </div>
              <p style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '12px' }}>
                {language === 'fr'
                  ? '⚠️ Copiez cette clé maintenant. Elle ne sera plus affichée après fermeture.'
                  : '⚠️ Copy this key now. It will not be shown again.'}
              </p>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <code style={{
                  flex: 1,
                  padding: '10px 12px',
                  background: 'var(--bg)',
                  borderRadius: '6px',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  wordBreak: 'break-all',
                }}>
                  {showNewKey ? newlyCreatedKey : newlyCreatedKey.substring(0, 15) + '•'.repeat(30)}
                </code>
                <button onClick={() => setShowNewKey(!showNewKey)} className={styles.buttonSecondary}>
                  {showNewKey ? <Icons.EyeOff size={14} /> : <Icons.Eye size={14} />}
                </button>
                <button onClick={copyApiKey} className={styles.buttonPrimary} style={{ background: keyCopied ? '#10b981' : 'var(--primary)' }}>
                  {keyCopied ? <Icons.CheckCircle size={14} /> : <Icons.Copy size={14} />}
                  <span style={{ marginLeft: '6px' }}>{keyCopied ? (language === 'fr' ? 'Copié !' : 'Copied!') : (language === 'fr' ? 'Copier' : 'Copy')}</span>
                </button>
              </div>
              <button
                onClick={() => { setNewlyCreatedKey(null); setShowNewKey(false); }}
                style={{ marginTop: '12px', background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: '13px' }}
              >
                {language === 'fr' ? 'Fermer' : 'Dismiss'}
              </button>
            </div>
          )}

          {/* API keys list */}
          {isLoadingKeys ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--muted)' }}>
              <Icons.Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
              {language === 'fr' ? 'Chargement...' : 'Loading...'}
            </div>
          ) : apiKeys.length === 0 ? (
            <div style={{ padding: '24px', background: 'var(--bg)', borderRadius: '8px', textAlign: 'center', color: 'var(--muted)' }}>
              <Icons.Key size={32} style={{ opacity: 0.5, marginBottom: '12px' }} />
              <p>{language === 'fr' ? 'Aucune clé API. Créez-en une pour commencer.' : 'No API keys. Create one to get started.'}</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {apiKeys.map(key => (
                <div key={key.id} style={{
                  padding: '16px',
                  background: 'var(--bg)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  flexWrap: 'wrap',
                }}>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {key.name}
                      {!key.is_active && (
                        <span style={{ fontSize: '11px', padding: '2px 6px', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', borderRadius: '4px' }}>
                          {language === 'fr' ? 'Désactivée' : 'Disabled'}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--muted)', fontFamily: 'monospace' }}>
                      {key.key_prefix}
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--muted)', textAlign: 'right' }}>
                    <div>{language === 'fr' ? 'Utilisations' : 'Usage'}: {key.usage_count}</div>
                    <div>{language === 'fr' ? 'Dernière utilisation' : 'Last used'}: {key.last_used_at ? new Date(key.last_used_at).toLocaleDateString() : '-'}</div>
                  </div>
                  <button
                    onClick={() => handleRevokeApiKey(key.id)}
                    className={styles.buttonSecondary}
                    style={{ padding: '8px 12px', color: '#ef4444', borderColor: '#ef4444' }}
                  >
                    <Icons.Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* API Documentation link */}
          <div style={{ marginTop: '16px' }}>
            <button onClick={onNavigateToAPI} className={styles.buttonSecondary} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Icons.FileText size={14} />
              {language === 'fr' ? 'Documentation API' : 'API Documentation'}
              <Icons.ExternalLink size={12} />
            </button>
          </div>
        </div>
      )}

      {/* Section Label for Preferences */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '20px',
        paddingBottom: '12px',
        borderBottom: '1px solid var(--border)',
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(139, 92, 246, 0.1))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Icons.Settings size={18} style={{ color: 'var(--primary)' }} />
        </div>
        <h2 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text)', margin: 0 }}>
          {language === 'fr' ? 'Préférences' : 'Preferences'}
        </h2>
      </div>

      {/* Grid Layout for other settings */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
        gap: '20px',
        marginBottom: '20px'
      }}>

        {/* Appearance Section */}
        <div className={styles.card}>
          <h2 className={styles.sectionTitle}>
            <Icons.Layout size={20} style={{ color: '#ec4899' }} />
            {t('settings.appearance')}
          </h2>

          {/* Theme Toggle */}
          <div style={{ padding: '16px', background: 'var(--bg)', borderRadius: '8px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  {isDark ? <Icons.Moon size={20} style={{ color: 'var(--primary)' }} /> : <Icons.Sun size={20} style={{ color: 'var(--primary)' }} />}
                  <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>{t('settings.theme')}</h3>
                </div>
                <p style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: '1.4' }}>
                  {isDark ? t('settings.darkModeEnabled') : t('settings.lightModeEnabled')}
                </p>
              </div>
              <label style={{ position: 'relative', display: 'inline-block', width: '52px', height: '28px', cursor: 'pointer' }}>
                <input type="checkbox" checked={isDark} onChange={() => { toggleDarkMode(); }} aria-label={t('settings.theme')} style={{ opacity: 0, width: 0, height: 0 }} />
                <span style={{ position: 'absolute', inset: 0, background: isDark ? 'var(--primary)' : 'var(--border)', borderRadius: '28px', transition: 'all 0.3s', cursor: 'pointer' }}>
                  <span style={{ position: 'absolute', height: '22px', width: '22px', left: isDark ? '27px' : '3px', bottom: '3px', background: 'white', borderRadius: '50%', transition: 'all 0.3s', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
                </span>
              </label>
            </div>
          </div>

          {/* User Mode Toggle */}
          <div style={{ padding: '16px', background: 'var(--bg)', borderRadius: '8px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  {userMode === 'expert' ? <Icons.Sparkles size={20} style={{ color: 'var(--primary)' }} /> : <Icons.Eye size={20} style={{ color: 'var(--primary)' }} />}
                  <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>{t('settings.displayMode')}</h3>
                </div>
                <p style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: '1.4' }}>
                  {userMode === 'simple' ? `${t('settings.simpleMode')} : ${t('settings.simpleDesc')}` : `${t('settings.expertMode')} : ${t('settings.expertDesc')}`}
                </p>
              </div>
              <label style={{ position: 'relative', display: 'inline-block', width: '52px', height: '28px', cursor: 'pointer' }}>
                <input type="checkbox" checked={userMode === 'expert'} onChange={() => { const newMode = userMode === 'simple' ? 'expert' : 'simple'; setUserMode(newMode); }} aria-label={t('settings.displayMode')} style={{ opacity: 0, width: 0, height: 0 }} />
                <span style={{ position: 'absolute', inset: 0, background: userMode === 'expert' ? 'var(--primary)' : 'var(--border)', borderRadius: '28px', transition: 'all 0.3s', cursor: 'pointer' }}>
                  <span style={{ position: 'absolute', height: '22px', width: '22px', left: userMode === 'expert' ? '27px' : '3px', bottom: '3px', background: 'white', borderRadius: '50%', transition: 'all 0.3s', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
                </span>
              </label>
            </div>
          </div>

          {/* Currency Selector */}
          <div style={{ padding: '16px', background: 'var(--bg)', borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <Icons.DollarSign size={20} style={{ color: 'var(--primary)' }} />
                  <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>{t('settings.currency')}</h3>
                </div>
                <p style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: '1.4' }}>{t('settings.currencyDesc')}</p>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {(['USD', 'EUR'] as Currency[]).map((curr) => (
                  <button
                    key={curr}
                    onClick={() => { setCurrency(curr); }}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: currency === curr ? '2px solid var(--primary)' : '1px solid var(--border)',
                      background: currency === curr ? 'rgba(139, 92, 246, 0.1)' : 'var(--card-bg)',
                      color: currency === curr ? 'var(--primary)' : 'var(--text)',
                      fontSize: '14px',
                      fontWeight: currency === curr ? 600 : 400,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>{CURRENCIES[curr].symbol}</span>
                    {curr}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Language Selector - Mobile Portrait only (in landscape it's in the top bar) */}
          {isMobile && isPortrait && (
            <div style={{ padding: '16px', background: 'var(--bg)', borderRadius: '8px', marginTop: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <Icons.Globe size={20} style={{ color: 'var(--primary)' }} />
                    <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>
                      {language === 'fr' ? 'Langue' : 'Language'}
                    </h3>
                  </div>
                  <p style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: '1.4' }}>
                    {language === 'fr' ? 'Choisissez la langue de l\'application' : 'Choose the application language'}
                  </p>
                </div>
                <LanguageSwitcher />
              </div>
            </div>
          )}
        </div>


        {/* Page Visibility Section */}
        <div className={styles.card}>
          <h2 className={styles.sectionTitle}>
            <Icons.Grid size={20} style={{ color: '#3b82f6' }} />
            {t('settings.pageVisibility')}
          </h2>
          <p style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '16px', lineHeight: '1.5' }}>{t('settings.pageVisibilityDesc')}</p>

          {/* Crypto.com Toggle */}
          <div style={{ padding: '16px', background: 'var(--bg)', borderRadius: '8px', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <Icons.Activity size={20} style={{ color: pageVisibility.cryptocom ? 'var(--primary)' : 'var(--muted)' }} />
                  <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>Crypto.com Dashboard</h3>
                </div>
                <p style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: '1.4' }}>{t('settings.showCryptocom')}</p>
              </div>
              <label style={{ position: 'relative', display: 'inline-block', width: '52px', height: '28px', cursor: 'pointer' }}>
                <input type="checkbox" checked={pageVisibility.cryptocom} onChange={(e) => { const isVisible = e.target.checked; setPageVisibility('cryptocom', isVisible); if (isVisible) { enableModule('cryptocom-dashboard'); } else { disableModule('cryptocom-dashboard'); } }} aria-label="Crypto.com Dashboard" style={{ opacity: 0, width: 0, height: 0 }} />
                <span style={{ position: 'absolute', inset: 0, background: pageVisibility.cryptocom ? 'var(--primary)' : 'var(--border)', borderRadius: '28px', transition: 'all 0.3s', cursor: 'pointer' }}>
                  <span style={{ position: 'absolute', height: '22px', width: '22px', left: pageVisibility.cryptocom ? '27px' : '3px', bottom: '3px', background: 'white', borderRadius: '50%', transition: 'all 0.3s', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
                </span>
              </label>
            </div>
          </div>

          {/* KuCoin Toggle */}
          <div style={{ padding: '16px', background: 'var(--bg)', borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <Icons.KuCoin size={20} style={{ color: pageVisibility.kucoin ? 'var(--primary)' : 'var(--muted)' }} />
                  <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>KuCoin Dashboard</h3>
                </div>
                <p style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: '1.4' }}>{t('settings.showKucoin')}</p>
              </div>
              <label style={{ position: 'relative', display: 'inline-block', width: '52px', height: '28px', cursor: 'pointer' }}>
                <input type="checkbox" checked={pageVisibility.kucoin} onChange={(e) => { const isVisible = e.target.checked; setPageVisibility('kucoin', isVisible); if (isVisible) { enableModule('kucoin-dashboard'); } else { disableModule('kucoin-dashboard'); } }} aria-label="KuCoin Dashboard" style={{ opacity: 0, width: 0, height: 0 }} />
                <span style={{ position: 'absolute', inset: 0, background: pageVisibility.kucoin ? 'var(--primary)' : 'var(--border)', borderRadius: '28px', transition: 'all 0.3s', cursor: 'pointer' }}>
                  <span style={{ position: 'absolute', height: '22px', width: '22px', left: pageVisibility.kucoin ? '27px' : '3px', bottom: '3px', background: 'white', borderRadius: '50%', transition: 'all 0.3s', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Cloud Sync - Enhanced section for non-authenticated users */}
      {!isAuthenticated && (
        <div style={{ marginBottom: '20px' }}>
          <div
            className={styles.card}
            style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(16, 185, 129, 0.02))',
              border: '1px solid rgba(16, 185, 129, 0.2)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Icons.Cloud size={20} color="white" />
              </div>
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text)', margin: 0 }}>
                  {language === 'fr' ? 'Synchronisation Cloud' : 'Cloud Sync'}
                </h2>
                <p style={{ fontSize: '13px', color: 'var(--muted)', margin: 0 }}>
                  {language === 'fr' ? 'Sauvegardez vos données localement ou dans le cloud' : 'Backup your data locally or in the cloud'}
                </p>
              </div>
            </div>
            <CloudSyncSettings />
          </div>
        </div>
      )}

      {/* FluxNode Hosting Section - Only show if user has subscriptions */}
      {isAuthenticated && hostingSubscriptions.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(99, 102, 241, 0.1))',
          border: '1px solid rgba(139, 92, 246, 0.2)',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '20px'
        }}>
          <h2 className={styles.sectionTitle}>
            <Icons.Server size={20} style={{ color: '#8b5cf6' }} />
            {t('fluxNode.hosting.settingsSection.title')}
          </h2>

          <p style={{ fontSize: '14px', color: 'var(--muted)', lineHeight: '1.6', marginBottom: '20px' }}>
            {t('fluxNode.hosting.settingsSection.description')}
          </p>

          {/* Subscriptions Summary */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
            <div style={{
              padding: '12px 16px',
              background: 'var(--bg)',
              borderRadius: '8px',
              flex: '1',
              minWidth: '120px',
            }}>
              <div style={{ fontSize: '24px', fontWeight: 700, color: hasActiveSubscription ? '#10b981' : '#f59e0b' }}>
                {hostingSubscriptions.length}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                {t('fluxNode.hosting.settingsSection.subscriptionsCount')}
              </div>
            </div>
            <div style={{
              padding: '12px 16px',
              background: 'var(--bg)',
              borderRadius: '8px',
              flex: '1',
              minWidth: '120px',
            }}>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#8b5cf6' }}>
                {hostingSubscriptions.reduce((sum, s) => sum + (s.nodes?.length || 0), 0)}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                {t('fluxNode.hosting.settingsSection.hostedNodesCount')}
              </div>
            </div>
            <div style={{
              padding: '12px 16px',
              background: 'var(--bg)',
              borderRadius: '8px',
              flex: '1',
              minWidth: '120px',
            }}>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#3b82f6' }}>
                {hostingSubscriptions.filter(s => s.status === 'active').length}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                {t('fluxNode.hosting.settingsSection.activeCount')}
              </div>
            </div>
          </div>

          {/* Link to FluxNode Monitor */}
          <a
            href="/fluxnode-monitor#hosting"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
              color: 'white',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: 600,
              textDecoration: 'none',
              boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)'
            }}
          >
            <Icons.ExternalLink size={18} />
            {t('fluxNode.hosting.settingsSection.manageNodes')}
          </a>
        </div>
      )}

      {/* Support Section */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(236, 72, 153, 0.1))',
        border: '1px solid rgba(239, 68, 68, 0.2)',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '20px'
      }}>
        <h2 className={styles.sectionTitle}>
          <Icons.Heart size={20} style={{ color: '#ef4444' }} />
          {language === 'fr' ? 'Soutenir le projet' : 'Support the project'}
        </h2>

        <p style={{ fontSize: '14px', color: 'var(--muted)', lineHeight: '1.6', marginBottom: '20px' }}>
          {language === 'fr'
            ? 'Edouard.ai est 100% gratuit et le restera toujours. Si vous appréciez ce projet et souhaitez soutenir son développement, vous pouvez faire un don via Ko-fi.'
            : 'Edouard.ai is 100% free and will always be. If you enjoy this project and want to support its development, you can make a donation via Ko-fi.'}
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          <a
            href="https://ko-fi.com/edouardai"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              background: '#FF5E5B',
              color: 'white',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: 600,
              textDecoration: 'none',
              boxShadow: '0 2px 8px rgba(255, 94, 91, 0.3)'
            }}
          >
            <Icons.Coffee size={18} />
            {language === 'fr' ? 'Offrir un café' : 'Buy me a coffee'}
          </a>
        </div>
      </div>

      {/* Keyframes for spinner */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Confirmation Modal */}
      <ConfirmationModal {...confirmationProps} />

      {/* Auth Modal */}
      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
    </div>
  );
}
